/**
 * AI Recruit - Assignment Hook
 * React Query hooks for assignment tracking and audit trail
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import * as assignmentApi from '../services/assignmentApi'

// ============================================
// MUTATION HOOKS
// ============================================

/**
 * Hook for updating resume assignment
 */
export const useUpdateResumeAssignment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resumeId, userId, reason }: { resumeId: string; userId: string; reason?: string }) =>
      assignmentApi.updateResumeAssignment(resumeId, userId, reason),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['resume'] })
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      queryClient.invalidateQueries({ queryKey: ['audit-history'] })
    },
  })
}

/**
 * Hook for updating job match assignment
 */
export const useUpdateJobMatchAssignment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ jobMatchId, userId, reason }: { jobMatchId: string; userId: string; reason?: string }) =>
      assignmentApi.updateJobMatchAssignment(jobMatchId, userId, reason),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['job'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      queryClient.invalidateQueries({ queryKey: ['job-matches'] })
      queryClient.invalidateQueries({ queryKey: ['candidate-matches'] })
      queryClient.invalidateQueries({ queryKey: ['audit-history'] })
    },
  })
}

// ============================================
// QUERY HOOKS
// ============================================

/**
 * Hook for fetching audit history with pagination
 */
export const useAuditHistory = (
  type: 'resume_upload' | 'job_match',
  entityId: string,
  page: number = 1,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['audit-history', type, entityId, page],
    queryFn: () => assignmentApi.getAuditHistory(type, entityId, page, 5),
    enabled: enabled && !!entityId,
  })
}

/**
 * Hook for fetching organization users with debounced search
 */
export const useOrganizationUsers = (searchTerm: string = '', enabled: boolean = true) => {
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm)

  // Debounce search term (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  return useQuery({
    queryKey: ['organization-users', debouncedSearch],
    queryFn: () => assignmentApi.getOrganizationUsers(debouncedSearch || undefined, 50),
    enabled,
  })
}
